import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Settings,
  Globe,
  Shield,
  Copy,
  ExternalLink,
  Play,
  Check,
  AlertTriangle,
  Plus,
  Trash2,
  Database,
  Key
} from 'lucide-react';
import api, { API_BASE_URL } from '../utils/api';


const WebhookApiWizard = ({ isOpen, onClose, user }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState({
    webhooks: {
      endpoints: [],
      enabled: true
    },
    apiAccess: {
      enableWebhooks: true,
      enableApiEndpoints: true,
      rateLimits: 'standard',
      apiKey: '',
      description: ''
    },
    testResults: {}
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const steps = [
    {
      id: 1,
      title: 'Welcome',
      description: 'Setup your webhook & API access',
      icon: <Settings className="w-5 h-5" />
    },
    {
      id: 2,
      title: 'Webhook Endpoints',
      description: 'Configure webhook destinations',
      icon: <Globe className="w-5 h-5" />
    },
    {
      id: 3,
      title: 'API Configuration',
      description: 'Setup API access & keys',
      icon: <Key className="w-5 h-5" />
    },
    {
      id: 4,
      title: 'Test & Validate',
      description: 'Verify your setup',
      icon: <Play className="w-5 h-5" />
    },
    {
      id: 5,
      title: 'Complete',
      description: 'Setup summary',
      icon: <CheckCircle className="w-5 h-5" />
    }
  ];

  const availableEvents = [
    'task.created',
    'task.updated', 
    'task.completed',
    'task.deleted',
    'project.created',
    'project.updated',
    'project.completed',
    'client.created',
    'client.updated',
    'comment.added',
    'user.invited',
    'integration.connected'
  ];

  useEffect(() => {
    if (isOpen) {
      // Generate API key when wizard opens
      const apiKey = `athena_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSetupData(prev => ({
        ...prev,
        apiAccess: {
          ...prev.apiAccess,
          apiKey: apiKey
        }
      }));
    }
  }, [isOpen]);

  const handleInputChange = (section, field, value) => {
    setSetupData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Clear related errors
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: null
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 2: // Webhook Endpoints
        setupData.webhooks.endpoints.forEach((endpoint, index) => {
          if (!endpoint.url.trim()) {
            newErrors[`webhook.${index}.url`] = 'Webhook URL is required';
          } else if (!isValidUrl(endpoint.url)) {
            newErrors[`webhook.${index}.url`] = 'Please enter a valid URL';
          }
          if (endpoint.events.length === 0) {
            newErrors[`webhook.${index}.events`] = 'At least one event must be selected';
          }
        });
        break;
      case 3: // API Configuration
        if (!setupData.apiAccess.description.trim()) {
          newErrors['apiAccess.description'] = 'API description is required';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const addWebhookEndpoint = () => {
    setSetupData(prev => ({
      ...prev,
      webhooks: {
        ...prev.webhooks,
        endpoints: [
          ...prev.webhooks.endpoints,
          {
            id: Date.now(),
            name: '',
            url: '',
            events: [],
            headers: {},
            description: ''
          }
        ]
      }
    }));
  };

  const removeWebhookEndpoint = (id) => {
    setSetupData(prev => ({
      ...prev,
      webhooks: {
        ...prev.webhooks,
        endpoints: prev.webhooks.endpoints.filter(endpoint => endpoint.id !== id)
      }
    }));
  };

  const updateWebhookEndpoint = (id, field, value) => {
    setSetupData(prev => ({
      ...prev,
      webhooks: {
        ...prev.webhooks,
        endpoints: prev.webhooks.endpoints.map(endpoint =>
          endpoint.id === id ? { ...endpoint, [field]: value } : endpoint
        )
      }
    }));
  };

  const handleTestWebhook = async (endpointId) => {
    setLoading(true);
    try {
      const endpoint = setupData.webhooks.endpoints.find(e => e.id === endpointId);
      
      // Send test webhook
      const testPayload = {
        event: 'test.webhook',
        timestamp: new Date().toISOString(),
        data: {
          message: 'This is a test webhook from Athena',
          test_id: Math.random().toString(36).substr(2, 9)
        }
      };

      const response = await fetch(endpoint.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'Athena-Webhook-Test/1.0',
          ...endpoint.headers
        },
        body: JSON.stringify(testPayload)
      });

      setSetupData(prev => ({
        ...prev,
        testResults: {
          ...prev.testResults,
          [`webhook_${endpointId}`]: {
            success: response.ok,
            status: response.status,
            message: response.ok ? 'Test webhook sent successfully' : `HTTP ${response.status}: ${response.statusText}`,
            timestamp: new Date().toISOString()
          }
        }
      }));
    } catch (error) {
      setSetupData(prev => ({
        ...prev,
        testResults: {
          ...prev.testResults,
          [`webhook_${endpointId}`]: {
            success: false,
            message: error.message || 'Failed to send test webhook',
            timestamp: new Date().toISOString()
          }
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleFinishSetup = async () => {
    setLoading(true);
    try {
      // Save webhook subscriptions
      for (const endpoint of setupData.webhooks.endpoints) {
        if (endpoint.url && endpoint.events.length > 0) {
          await api.post(`/webhooks/subscriptions`, {
            name: endpoint.name || 'Custom Webhook',
            url: endpoint.url,
            events: endpoint.events,
            headers: endpoint.headers || {},
            active: true
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}`
            }
          });
        }
      }

      // Save API access configuration (this would need a backend endpoint)
      // TODO: Implement API key storage endpoint
      
      // Success
      setCurrentStep(5);
    } catch (error) {
      console.error('Setup failed:', error);
      alert('Setup failed. Please check your configuration and try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Globe className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Webhook & API Setup Wizard</h2>
            </div>
            <div className="text-sm text-gray-500">
              Step {currentStep} of {steps.length}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep > step.id 
                    ? 'bg-green-100 border-green-500 text-green-600' 
                    : currentStep === step.id 
                      ? 'bg-blue-100 border-blue-500 text-blue-600' 
                      : 'bg-gray-100 border-gray-300 text-gray-400'
                }`}>
                  {currentStep > step.id ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    step.icon
                  )}
                </div>
                <div className="ml-2 text-sm">
                  <div className={`font-medium ${
                    currentStep >= step.id ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`mx-4 w-12 h-0.5 ${
                    currentStep > step.id ? 'bg-green-300' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Setup Your Webhook & API Integration</h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                This wizard will help you configure webhook endpoints to receive real-time notifications from Athena,
                and set up API access for external applications to interact with your data.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <Globe className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-1">Webhook Endpoints</h4>
                  <p className="text-sm text-gray-600">Configure URLs to receive event notifications</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <Key className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-1">API Access</h4>
                  <p className="text-sm text-gray-600">Generate API keys for external integrations</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-left">
                    <h4 className="text-sm font-medium text-blue-900 mb-1">What You'll Set Up</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Custom webhook endpoints for external services</li>
                      <li>• Event subscriptions for real-time notifications</li>
                      <li>• API keys for secure external access</li>
                      <li>• Rate limiting and security settings</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Webhook Endpoints */}
          {currentStep === 2 && (
            <div>
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Configure Webhook Endpoints</h3>
                <p className="text-gray-600">
                  Set up webhook endpoints to receive real-time notifications when events occur in Athena.
                </p>
              </div>

              <div className="space-y-6">
                {setupData.webhooks.endpoints.map((endpoint, index) => (
                  <div key={endpoint.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900">Webhook #{index + 1}</h4>
                      <button
                        onClick={() => removeWebhookEndpoint(endpoint.id)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={endpoint.name}
                          onChange={(e) => updateWebhookEndpoint(endpoint.id, 'name', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., Zapier Integration"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL *</label>
                        <input
                          type="url"
                          value={endpoint.url}
                          onChange={(e) => updateWebhookEndpoint(endpoint.id, 'url', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="https://hooks.zapier.com/hooks/catch/..."
                        />
                        {errors[`webhook.${index}.url`] && (
                          <p className="text-sm text-red-600 mt-1">{errors[`webhook.${index}.url`]}</p>
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <input
                        type="text"
                        value={endpoint.description}
                        onChange={(e) => updateWebhookEndpoint(endpoint.id, 'description', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Brief description of what this webhook is for"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Events to Subscribe *</label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                        {availableEvents.map((event) => (
                          <label key={event} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={endpoint.events.includes(event)}
                              onChange={(e) => {
                                const newEvents = e.target.checked
                                  ? [...endpoint.events, event]
                                  : endpoint.events.filter(e => e !== event);
                                updateWebhookEndpoint(endpoint.id, 'events', newEvents);
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-gray-700">{event}</span>
                          </label>
                        ))}
                      </div>
                      {errors[`webhook.${index}.events`] && (
                        <p className="text-sm text-red-600 mt-1">{errors[`webhook.${index}.events`]}</p>
                      )}
                    </div>

                    {/* Custom Headers */}
                    <div className="border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Custom Headers (Optional)</h5>
                      <div className="text-xs text-gray-500 mb-2">
                        Add custom HTTP headers for authentication or identification
                      </div>
                      <textarea
                        value={JSON.stringify(endpoint.headers, null, 2)}
                        onChange={(e) => {
                          try {
                            const headers = JSON.parse(e.target.value);
                            updateWebhookEndpoint(endpoint.id, 'headers', headers);
                          } catch (error) {
                            // Invalid JSON - don't update
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs font-mono"
                        rows="3"
                        placeholder='{"Authorization": "Bearer your-token", "X-Custom-Header": "value"}'
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={addWebhookEndpoint}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-300 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Webhook Endpoint</span>
                </button>
              </div>
            </div>
          )}

          {/* Step 3: API Configuration */}
          {currentStep === 3 && (
            <div>
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">API Access Configuration</h3>
                <p className="text-gray-600">
                  Configure API access settings and generate API keys for external applications.
                </p>
              </div>

              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">API Key Generation</h4>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Key Description *
                    </label>
                    <input
                      type="text"
                      value={setupData.apiAccess.description}
                      onChange={(e) => handleInputChange('apiAccess', 'description', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., External CRM Integration, Mobile App Access"
                    />
                    {errors['apiAccess.description'] && (
                      <p className="text-sm text-red-600 mt-1">{errors['apiAccess.description']}</p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Generated API Key
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={setupData.apiAccess.apiKey}
                        readOnly
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 font-mono text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(setupData.apiAccess.apiKey)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Keep this API key secure. You'll need it to authenticate API requests.
                    </p>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">Webhook Events</h4>
                      <p className="text-sm text-gray-600">Allow webhook events to be sent to configured endpoints</p>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={setupData.apiAccess.enableWebhooks}
                        onChange={(e) => handleInputChange('apiAccess', 'enableWebhooks', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        setupData.apiAccess.enableWebhooks ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          setupData.apiAccess.enableWebhooks ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </div>
                    </label>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">API Endpoints</h4>
                      <p className="text-sm text-gray-600">Allow external applications to access your data via REST API</p>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={setupData.apiAccess.enableApiEndpoints}
                        onChange={(e) => handleInputChange('apiAccess', 'enableApiEndpoints', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        setupData.apiAccess.enableApiEndpoints ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          setupData.apiAccess.enableApiEndpoints ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </div>
                    </label>
                  </div>
                  {setupData.apiAccess.enableApiEndpoints && (
                    <div className="pl-4 border-l-2 border-blue-100">
                      <p className="text-sm text-gray-600 mb-2">
                        External applications can read and modify your data using standard REST API endpoints.
                      </p>
                      <div className="text-xs text-gray-500">
                        Base URL: <code className="bg-gray-100 px-1 rounded">{API_BASE_URL}</code>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Rate Limiting</h4>
                  <div className="space-y-3">
                    {[
                      { value: 'standard', label: 'Standard (100 requests/minute)', description: 'Suitable for most integrations' },
                      { value: 'high', label: 'High Volume (500 requests/minute)', description: 'For frequent data synchronization' },
                      { value: 'unlimited', label: 'Unlimited', description: 'No rate limiting (use with caution)' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="rateLimits"
                          value={option.value}
                          checked={setupData.apiAccess.rateLimits === option.value}
                          onChange={(e) => handleInputChange('apiAccess', 'rateLimits', e.target.value)}
                          className="mt-0.5 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{option.label}</div>
                          <div className="text-xs text-gray-600">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Test & Validate */}
          {currentStep === 4 && (
            <div>
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Test & Validate Your Setup</h3>
                <p className="text-gray-600">
                  Test your webhook endpoints and verify your API configuration.
                </p>
              </div>

              <div className="space-y-6">
                {/* Webhook Tests */}
                {setupData.webhooks.endpoints.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <Globe className="w-6 h-6 text-blue-500" />
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">Webhook Endpoints</h4>
                        <p className="text-sm text-gray-600">Test your configured webhook endpoints</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {setupData.webhooks.endpoints.map((endpoint, index) => (
                        <div key={endpoint.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-900">
                                {endpoint.name || `Webhook ${index + 1}`}
                              </span>
                              <span className="text-xs text-gray-600">
                                {endpoint.events.length} events
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{endpoint.url}</div>
                            
                            {setupData.testResults[`webhook_${endpoint.id}`] && (
                              <div className={`mt-2 p-2 rounded text-xs ${
                                setupData.testResults[`webhook_${endpoint.id}`].success 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {setupData.testResults[`webhook_${endpoint.id}`].message}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleTestWebhook(endpoint.id)}
                            disabled={loading}
                            className="ml-3 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {loading ? 'Testing...' : 'Test'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* API Configuration Summary */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <Key className="w-6 h-6 text-green-500" />
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">API Configuration</h4>
                      <p className="text-sm text-gray-600">Your API access settings</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${setupData.apiAccess.enableWebhooks ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span>Webhooks {setupData.apiAccess.enableWebhooks ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${setupData.apiAccess.enableApiEndpoints ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span>API Access {setupData.apiAccess.enableApiEndpoints ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span>Rate Limit: {setupData.apiAccess.rateLimits}</span>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-gray-50 rounded text-xs">
                    <strong>API Key:</strong> {setupData.apiAccess.description}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {currentStep === 5 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Setup Complete!</h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Your webhook and API integration has been successfully configured. External applications can now
                receive real-time notifications and access your Athena data securely.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
                {setupData.webhooks.endpoints.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <Globe className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-900 mb-1">Webhooks</h4>
                    <p className="text-sm text-gray-600">{setupData.webhooks.endpoints.length} endpoints configured</p>
                  </div>
                )}
                {setupData.apiAccess.enableApiEndpoints && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <Key className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-gray-900 mb-1">API Access</h4>
                    <p className="text-sm text-gray-600">API key generated and configured</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">What's Next?</h4>
                <ul className="text-sm text-gray-600 text-left max-w-md mx-auto space-y-1">
                  <li>• Monitor webhook delivery logs in the Workspace settings</li>
                  <li>• Use your API key to authenticate external requests</li>
                  <li>• Configure additional webhooks as your integrations grow</li>
                  <li>• Check the API documentation for available endpoints</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </button>

          <div className="text-sm text-gray-500">
            {currentStep} of {steps.length}
          </div>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={currentStep === 4 ? handleFinishSetup : handleNext}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {currentStep === 4 ? (loading ? 'Setting up...' : 'Finish Setup') : 'Next'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebhookApiWizard;
